import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { openDeepLink } from '../lib/shareTokens'
import type { ConfirmationType } from '../types/auth'
import StoreBadge from '../components/share/StoreBadge'

interface LocationState {
  type?: ConfirmationType
}

function buildAuthDeepLink(type: ConfirmationType, session: Session): string {
  // Tokens go in the URL fragment so they're never sent to any server.
  // The mobile app reads them from the deep link and calls supabase.auth.setSession().
  const params = new URLSearchParams({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    token_type: session.token_type,
    expires_in: String(session.expires_in),
    type,
  })
  return `stazon://email-confirm#${params.toString()}`
}

function ConfirmEmail() {
  const location = useLocation()
  const [isVerified, setIsVerified] = useState(false)
  const [loading, setLoading] = useState(true)
  const [email, setEmail] = useState<string | null>(null)
  const [session, setSession] = useState<Session | null>(null)

  const state = location.state as LocationState | null
  const confirmationType = state?.type || 'signup'

  useEffect(() => {
    const checkSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession()
      if (session?.user) {
        setIsVerified(true)
        setEmail(session.user.email || null)
        setSession(session)
      }
      setLoading(false)
    }
    checkSession()
  }, [])

  const getTitle = () => {
    switch (confirmationType) {
      case 'email_change':
        return 'Email Updated'
      case 'magiclink':
        return 'Signed In'
      default:
        return 'Email Confirmed'
    }
  }

  const getMessage = () => {
    switch (confirmationType) {
      case 'email_change':
        return 'Your email address has been successfully updated.'
      case 'magiclink':
        return 'You have been successfully signed in.'
      default:
        return 'Your email has been verified and your account is now active.'
    }
  }

  const handleOpenApp = () => {
    if (!session) return
    openDeepLink(buildAuthDeepLink(confirmationType, session))
  }

  if (loading) {
    return (
      <div className="card">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Verifying...</p>
        </div>
      </div>
    )
  }

  if (!isVerified) {
    return (
      <div className="card">
        <div className="error-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <div className="card-header">
          <h1>Verification Failed</h1>
          <p>We couldn't verify your email.</p>
        </div>
        <div className="alert alert-error">
          The verification link may have expired or already been used. Please
          request a new verification email from the app.
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="success-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
      <div className="card-header">
        <h1>{getTitle()}</h1>
        <p>{getMessage()}</p>
      </div>
      {email && (
        <div className="alert alert-success">
          {confirmationType === 'email_change'
            ? `Your email is now ${email}`
            : `Welcome! Your account (${email}) is ready to use.`}
        </div>
      )}
      {session && (
        <button
          type="button"
          className="btn btn-primary mt-4"
          onClick={handleOpenApp}
          data-testid="open-in-app"
        >
          Open in Stazon app
        </button>
      )}
      <p className="text-center mt-4">Or close this page and return to the app.</p>
      {/* ON-407: no app yet? Get it — the account works there straight away. */}
      <p className="text-center mt-4">Don't have the app yet?</p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 8 }} data-testid="store-badges">
        <StoreBadge platform="ios" height={40} />
        <StoreBadge platform="android" height={40} />
      </div>
    </div>
  )
}

export default ConfirmEmail
